import { httpsCallable } from 'firebase/functions';
import { collection, getDocs, query, where } from 'firebase/firestore';
import { functions, db, auth } from './init';

/*
 * Client wrappers for the capability-token sharing Cloud Functions (functions/index.js). The secret
 * view/edit tokens live server-side in shareLinks/{id} (client-unreadable), so every shared read/write
 * is mediated by these callables.
 */

export interface ShareTokens {
  viewToken: string;
  editToken: string;
}

export interface SharedSketchResult {
  capability: 'view' | 'edit';
  project: any;
}

/** Owner: enable link sharing for a sketch → { viewToken, editToken } (idempotent). */
export async function enableSharing(projectId: string): Promise<ShareTokens> {
  const fn = httpsCallable(functions, 'enableSharing');
  const res = await fn({ projectId });
  return res.data as ShareTokens;
}

/** Owner: disable link sharing (existing links stop working). */
export async function disableSharing(projectId: string): Promise<void> {
  const fn = httpsCallable(functions, 'disableSharing');
  await fn({ projectId });
}

/** Open a shared sketch via a token → { capability, project }. */
export async function getSharedSketch(
  projectId: string,
  token: string
): Promise<SharedSketchResult> {
  const fn = httpsCallable(functions, 'getSharedSketch');
  const res = await fn({ projectId, token });
  return res.data as SharedSketchResult;
}

export interface SharedWithMeItem {
  projectId: string;
  ownerUsername: string;
  name: string;
  capability: 'view' | 'edit';
  token: string;
  updatedAt?: number;
}

/** Remove a sketch from the current user's "Shared with me" list. */
export async function removeSharedAccess(projectId: string): Promise<void> {
  const fn = httpsCallable(functions, 'removeSharedAccess');
  await fn({ projectId });
}

/** List the sketches shared WITH the current user (recorded when they opened a share link). */
export async function listSharedWithMe(): Promise<SharedWithMeItem[]> {
  const uid = auth.currentUser?.uid;
  if (!uid) return [];
  const q = query(collection(db, 'sharedAccess'), where('userId', '==', uid));
  const snap = await getDocs(q);
  const items = snap.docs.map((d) => d.data() as any);
  // Sort newest-first client-side (avoids a composite index on userId+updatedAt).
  items.sort(
    (a, b) =>
      (b.updatedAt?.toMillis?.() ?? 0) - (a.updatedAt?.toMillis?.() ?? 0)
  );
  return items as SharedWithMeItem[];
}

function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result as string; // data:<type>;base64,<data>
      resolve(result.slice(result.indexOf(',') + 1));
    };
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(file);
  });
}

/**
 * Upload a media asset into the sketch OWNER's storage on behalf of an edit-link holder (server
 * validates the edit token). Returns the Firebase download URL.
 */
export async function uploadSharedAsset(
  projectId: string,
  token: string,
  file: File
): Promise<{ url: string; path: string; size: number }> {
  const dataBase64 = await fileToBase64(file);
  const fn = httpsCallable(functions, 'uploadSharedAsset');
  const res = await fn({
    projectId,
    token,
    fileName: file.name,
    contentType: file.type,
    dataBase64
  });
  return res.data as { url: string; path: string; size: number };
}

/** Save a shared sketch using the edit token (server-validated). Returns the fresh project. */
export async function saveSharedSketch(
  projectId: string,
  token: string,
  changes: { name?: string; files?: any[]; visibility?: string },
  knownUpdatedAt?: number
): Promise<{ project: any }> {
  const fn = httpsCallable(functions, 'saveSharedSketch');
  const res = await fn({ projectId, token, changes, knownUpdatedAt });
  return res.data as { project: any };
}
